<template>
  <!-- 只在开发环境显示主题切换器 -->
  <div v-if="isDevelopment" class="fixed bottom-4 left-4 z-50">
    <div class="bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-xs">
      <!-- 标题栏 -->
      <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
          </svg>
          <span class="text-sm font-semibold text-gray-700">主题切换</span>
        </div>
        <button
          @click="isExpanded = !isExpanded"
          class="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
            :class="{ 'rotate-180': isExpanded }"
            class="transition-transform"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>

      <!-- 展开内容 -->
      <div v-if="isExpanded" class="space-y-3">
        <!-- 专业主题组 -->
        <div>
          <div class="text-xs font-medium text-gray-500 mb-2">专业主题 ⭐</div>
          <div class="grid grid-cols-2 gap-2">
            <button 
              v-for="theme in professionalThemes"
              :key="theme.key"
              @click="switchTheme(theme.key)"
              :class="[
                'px-2 py-1.5 text-xs rounded-md transition-all text-left relative',
                currentTheme === theme.key 
                  ? 'text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              ]"
              :style="currentTheme === theme.key ? { backgroundColor: theme.color } : {}"
              :title="theme.description"
            >
              <div class="flex items-center gap-1.5">
                <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: theme.color }"></span>
                <span class="font-medium">{{ theme.name }}</span>
              </div>
            </button>
          </div>
        </div>
        
        <!-- 原有主题组 -->
        <div>
          <div class="text-xs font-medium text-gray-500 mb-2">原有主题</div>
          <div class="flex gap-2">
            <button 
              @click="switchTheme('blue')"
              :class="[
                'px-3 py-1.5 text-xs rounded-md transition-colors flex-1',
                currentTheme === 'blue' 
                  ? 'bg-psychology-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              粉色
            </button>
            <button
              @click="switchTheme('green')"
              :class="[
                'px-3 py-1.5 text-xs rounded-md transition-colors flex-1',
                currentTheme === 'green'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              青绿色
            </button>
          </div>
        </div>
        
        <!-- 当前主题信息 -->
        <div class="pt-2 border-t border-gray-200">
          <div class="text-xs text-gray-500">
            当前: <span class="font-medium text-gray-700">{{ currentThemeName }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentTheme, applyTheme } from '@/utils/themeUtils'

export default {
  name: 'ThemeSwitcher',
  data() {
    return {
      isExpanded: false, // 默认收起，减少页面占用
      currentTheme: 'professional-blue',
      // 专业主题配置
      professionalThemes: [
        {
          key: 'professional-blue',
          name: '深蓝色',
          color: '#2563EB',
          description: '专业、可信、稳重'
        },
        {
          key: 'professional-green',
          name: '深绿色',
          color: '#059669',
          description: '成长、发展、专业'
        },
        {
          key: 'professional-purple',
          name: '紫色',
          color: '#7C3AED',
          description: '智慧、创新、专业'
        },
        {
          key: 'professional-orange',
          name: '橙色',
          color: '#EA580C',
          description: '活力、积极、职业导向'
        },
        {
          key: 'professional-indigo',
          name: '靛蓝色',
          color: '#4F46E5',
          description: '专业、现代、科技感'
        }
      ]
    }
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    currentThemeName() {
      const theme = this.professionalThemes.find(t => t.key === this.currentTheme)
      if (theme) return theme.name
      
      // 原有主题名称映射
      const legacyNames = {
        'blue': '粉色',
        'green': '青绿色'
      }
      return legacyNames[this.currentTheme] || this.currentTheme
    }
  },
  mounted() {
    this.currentTheme = getCurrentTheme().name
  },
  methods: {
    switchTheme(themeName) {
      // 在开发环境中，使用localStorage存储主题选择
      if (process.env.NODE_ENV === 'development') {
        localStorage.setItem('dev-theme', themeName)
        this.currentTheme = themeName
        
        // 重新应用主题
        applyTheme()
        
        // 提示信息
        const theme = this.professionalThemes.find(t => t.key === themeName)
        if (theme) {
          console.log(`✅ 已切换到: ${theme.name} - ${theme.description}`)
        }
      }
    }
  }
}
</script>
